import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { pePortfolioService, type PePortfolioMembershipDto } from '@/services/pePortfolioService';
import {
  getPeDelegatedChildTenantId,
  setPeDelegatedChildTenantId,
  subscribePeDelegatedContext,
} from '@/services/peDelegatedContext';
import { useAuth } from '@/state/auth/AuthProvider';

/**
 * Persistent strip when a portfolio company is selected (§18.8).
 */
export const PeDelegatedReadOnlyBanner: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [childId, setChildId] = useState<string | null>(() => getPeDelegatedChildTenantId());
  const [companies, setCompanies] = useState<PePortfolioMembershipDto[]>([]);

  useEffect(() => {
    return subscribePeDelegatedContext(() => setChildId(getPeDelegatedChildTenantId()));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!isAuthenticated || isLoading || user?.tenantType !== 'PORTFOLIO' || !childId) {
        setCompanies([]);
        return;
      }
      try {
        const ws = await pePortfolioService.getWorkspace();
        if (cancelled || !ws.viewPortfolio) {
          setCompanies([]);
          return;
        }
        const list = await pePortfolioService.listCompanies();
        if (!cancelled) {
          setCompanies(list);
        }
      } catch {
        if (!cancelled) {
          setCompanies([]);
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, user?.tenantType, childId]);

  const name = useMemo(() => {
    if (!childId) {
      return '';
    }
    const row = companies.find((c) => c.childTenantId === childId);
    return row?.childTenantName?.trim() || childId;
  }, [childId, companies]);

  const clearDelegated = useCallback(async () => {
    try {
      await pePortfolioService.postDelegatedContext(null);
      setPeDelegatedChildTenantId(null);
      const params = new URLSearchParams(location.search);
      params.delete('peContext');
      const qs = params.toString();
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e instanceof Error ? e.message : 'Could not clear view');
      toast.error(msg, { id: 'pe-delegated-clear' });
    }
  }, [location.pathname, location.search, navigate]);

  if (!childId) {
    return null;
  }

  const clearLabel = t('pePortfolio.delegated.clearView') || 'Clear';

  return (
    <div
      className="flex w-full items-center justify-center gap-3 border-b border-amber-500/40 bg-amber-500/15 px-4 py-2 text-sm text-amber-950 dark:text-amber-100"
      role="status"
    >
      <span>
        {(t('pePortfolio.delegated.bannerLine') || 'Viewing {company} — read-only.').replace('{company}', name)}
      </span>
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded border border-amber-700/40 px-2 py-0.5 text-xs font-medium hover:bg-amber-500/20"
        onClick={() => void clearDelegated()}
      >
        <XMarkIcon className="h-3.5 w-3.5" />
        {clearLabel}
      </button>
    </div>
  );
};
